import { ENV } from '../config/environment';
import { authenticatedFetch } from '../utils/authenticatedFetch';

type LogLevel = 'INFO' | 'WARN' | 'ERROR' | 'DEBUG';

interface LogPayload {
    level: LogLevel;
    module: string;
    message: string;
    data?: any;
    timestamp: string;
}

class LoggerService {
    private static instance: LoggerService;
    private gatewayUrl = ENV.GATEWAY_URL;

    private constructor() {}

    public static getInstance(): LoggerService {
        if (!LoggerService.instance) {
            LoggerService.instance = new LoggerService();
        }
        return LoggerService.instance;
    }

    private formatMessage(payload: LogPayload): string {
        const color = payload.level === 'ERROR' ? 'red' : payload.level === 'WARN' ? 'orange' : '#00ffff';
        return `%c[Wingman ${payload.level}] [${payload.module}] %c${payload.message}`;
    }

    private async shipToBackend(payload: LogPayload) {
        // Only ship WARN and ERROR to backend to save bandwidth
        if (payload.level !== 'ERROR' && payload.level !== 'WARN') return;

        try {
            await authenticatedFetch(`${this.gatewayUrl}/api/v1/ops/logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        } catch (e) {
            // Silently fail if backend is unreachable
        }
    }

    public info(module: string, message: string, data?: any) {
        const payload: LogPayload = { level: 'INFO', module, message, data, timestamp: new Date().toISOString() };
        console.log(this.formatMessage(payload), 'color: #00ffff; font-weight: bold;', 'color: white;', data || '');
        this.shipToBackend(payload);
    }

    public warn(module: string, message: string, data?: any) {
        const payload: LogPayload = { level: 'WARN', module, message, data, timestamp: new Date().toISOString() };
        console.warn(this.formatMessage(payload), 'color: orange; font-weight: bold;', 'color: white;', data || '');
        this.shipToBackend(payload);
    }

    public error(module: string, message: string, data?: any) {
        const payload: LogPayload = { level: 'ERROR', module, message, data, timestamp: new Date().toISOString() };
        console.error(this.formatMessage(payload), 'color: red; font-weight: bold;', 'color: white;', data || '');
        this.shipToBackend(payload);
    }

    public analytics(event: string, data?: any) {
        const payload: LogPayload = { level: 'INFO', module: 'ANALYTICS', message: event, data, timestamp: new Date().toISOString() };
        // Distinctive styling for analytics
        console.log(`%c📊 [ANALYTICS] %c${event}`, 'color: #ff00ff; font-weight: bold;', 'color: #00ffff;', data || '');
        this.shipToBackend(payload);
    }

    public debug(module: string, message: string, data?: any) {
        if (process.env.NODE_ENV === 'production') return;
        const payload: LogPayload = { level: 'DEBUG', module, message, data, timestamp: new Date().toISOString() };
        console.debug(this.formatMessage(payload), 'color: gray; font-weight: bold;', 'color: white;', data || '');
    }
}

export const wingmanLogger = LoggerService.getInstance();
